import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import type { Route } from "../../store/admin/routesStore";

export interface RouteListParams {
  page?: number;
  limit?: number;
  status_id?: string;
  search?: string;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface RouteListBody {
  data?: Route[];
  meta?: ListMeta;
}

export interface RouteListResult {
  data: Route[];
  meta?: ListMeta;
}

function cleanParams(params: RouteListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    page: params.page ?? 1,
    limit: params.limit ?? 6,
  };
  if (params.status_id) out.status_id = params.status_id;
  if (params.search) out.search = params.search;
  return out;
}

export function useRoutesList(
  params: RouteListParams = {},
  options: { enabled?: boolean } = {},
) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.routes.list(companyId, params),
    queryFn: ({ signal }) =>
      getJson<RouteListBody>("/rutasList", cleanParams(params), {
        signal,
      }).then((body) => ({ data: body.data ?? [], meta: body.meta })),
    enabled: options.enabled ?? true,
  });
}

interface RouteDetailBody {
  data?: { data?: Route };
}

export function useRoute(
  routeId: string | null,
  options: { enabled?: boolean } = {},
) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.routes.detail(companyId, routeId),
    queryFn: ({ signal }) =>
      getJson<RouteDetailBody>("/rutas", { ruta_id: routeId }, { signal }).then(
        (body) => body.data?.data ?? null,
      ),
    enabled: (options.enabled ?? true) && !!routeId,
    staleTime: 5 * 60 * 1000,
  });
}
